// Polls are ordinary encrypted messages. Votes are messages too, so the server cannot count them.
// Every device adds them up itself.

import type { ChatMessage } from "./client";

export type PollTally = {
  /** Votes per option, in option order. */
  counts: number[];
  /** Who chose each option, in option order. */
  voters: string[][];
  /** The options the viewer chose. */
  mine: number[];
  /** How many people have a vote in. */
  people: number;
};

/** Vote messages carry no text of their own, so the timeline leaves them out. */
export function isVote(m: ChatMessage): boolean {
  return m.content?.type === "vote";
}

/**
 * Adds up the votes for a poll. Each person's latest vote replaces their earlier ones, an empty
 * vote takes their vote back, and a single-choice poll counts only the first option.
 */
export function tallyPoll(poll: ChatMessage, messages: ChatMessage[], meId?: string): PollTally {
  const content = poll.content;
  const size = content?.type === "poll" ? content.options.length : 0;
  const multiple = content?.type === "poll" && Boolean(content.multiple);
  const latest = new Map<string, number[]>();
  // Messages still sending have no sequence number yet but are the newest.
  const ordered = [...messages].sort(
    (a, b) => (a.seq || Number.MAX_SAFE_INTEGER) - (b.seq || Number.MAX_SAFE_INTEGER),
  );
  for (const m of ordered) {
    const c = m.content;
    if (c?.type !== "vote" || c.poll_id !== poll.id || !m.senderId || m.deleted) continue;
    const valid = [...new Set(c.options)].filter((i) => Number.isInteger(i) && i >= 0 && i < size);
    latest.set(m.senderId, multiple ? valid : valid.slice(0, 1));
  }
  const counts = Array.from({ length: size }, () => 0);
  const voters: string[][] = Array.from({ length: size }, () => []);
  let people = 0;
  for (const [user, options] of latest) {
    if (options.length) people++;
    for (const i of options) {
      counts[i] = (counts[i] ?? 0) + 1;
      voters[i]?.push(user);
    }
  }
  return { counts, voters, mine: (meId && latest.get(meId)) || [], people };
}

/** What a person's vote becomes when they tap an option. */
export function nextVote(current: number[], option: number, multiple: boolean): number[] {
  if (multiple)
    return current.includes(option)
      ? current.filter((i) => i !== option)
      : [...current, option].sort((a, b) => a - b);
  return current.length === 1 && current[0] === option ? [] : [option];
}
